import { Injectable, inject } from '@angular/core';
import { ArticleService, ClientService } from '@frontend-workspace/shared/src';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, from, map, switchMap, tap } from 'rxjs';
import {
  addArticleClientMapping,
  addArticleClientMappingSuccess,
  getArticle,
  getArticlesByClientId,
  getArticlesByClientIdSuccess,
  getClientByArticleNumber,
  getClientByArticleNumberSuccess,
  getClientInfoWithFilters,
  getClientInfoWithFiltersSuccess,
  removeArticleClientMapping,
  removeArticleClientMappingSuccess,
  setArticle,
  setArticlesByClientIdLoading,
  setClientInfoFiltersLoading,
} from './articles.actions';

@Injectable()
export class ArticlesEffects {
  private actions$ = inject(Actions);
  private readonly _articleService = inject(ArticleService);
  private readonly _clientService = inject(ClientService);
  private readonly _store = inject(Store);

  getArticle$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(getArticle),
      switchMap(({ articleId, batchIds, rootArticleId, rootBatchId }) =>
        from(
          this._articleService.getArticle(
            articleId,
            batchIds,
            rootArticleId,
            rootBatchId,
          ),
        ).pipe(
          map((response) => response.data),
          map((article) => setArticle({ article })),
        ),
      ),
    ),
  );

  getClientByArticleNumber$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(getClientByArticleNumber),
      switchMap(({ articleNumber }) =>
        from(this._clientService.getClientByArticleNumber(articleNumber)).pipe(
          map((response) => response.data),
          map((clients) => getClientByArticleNumberSuccess({ clients })),
        ),
      ),
    ),
  );

  getArticlesByClientId$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(getArticlesByClientId),
      tap(() => {
        this._store.dispatch(setArticlesByClientIdLoading({ loading: true }));
      }),
      switchMap(({ clientId }) => {
        return from(this._clientService.getArticlesByClientId(clientId)).pipe(
          map((response) => response.data),
          map((articles) => getArticlesByClientIdSuccess({ articles })),
          tap(() => {
            this._store.dispatch(
              setArticlesByClientIdLoading({ loading: false }),
            );
          }),
        );
      }),
    ),
  );

  removeArticleClientMapping$ = createEffect((): Observable<Action> => {
    let _articleId: string | undefined;
    let _clientId: string | undefined;
    let _companyCode: string | undefined;

    return this.actions$.pipe(
      ofType(removeArticleClientMapping),
      switchMap(({ articleId, clientId, companyCode }) => {
        _articleId = articleId;
        _clientId = clientId;
        _companyCode = companyCode;

        return from(
          this._clientService.removeArticleClientMapping(
            articleId,
            clientId,
            companyCode,
          ),
        ).pipe(
          map((response) => response.data),
          map(() =>
            removeArticleClientMappingSuccess({
              articleId: _articleId,
              clientId: _clientId,
              companyCode: _companyCode,
            }),
          ),
        );
      }),
    );
  });

  addArticleClientMapping$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(addArticleClientMapping),
      switchMap(({ articleId, clientId, companyCode }) =>
        from(
          this._clientService.addArticleClientMapping(
            articleId,
            clientId,
            companyCode,
          ),
        ).pipe(
          map((response) => response.data),
          map((mapping) => addArticleClientMappingSuccess({ mapping })),
        ),
      ),
    ),
  );

  getClientInfoWithFilters$ = createEffect((): Observable<Action> =>
    this.actions$.pipe(
      ofType(getClientInfoWithFilters),
      tap(() => {
        this._store.dispatch(setClientInfoFiltersLoading({ loading: true }));
      }),
      switchMap(({ articleId, clientId, companyCode }) => {
        return from(
          this._clientService.getClientInfoWithFilters(
            articleId,
            clientId,
            companyCode,
          ),
        ).pipe(
          map((response) => response.data),
          map((clients) => getClientInfoWithFiltersSuccess({ clients })),
          tap(() => {
            this._store.dispatch(
              setClientInfoFiltersLoading({ loading: false }),
            );
          }),
        );
      }),
    ),
  );
}
